import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { pick, prop } from 'ramda';
import { log } from 'log';
import { useAppSelector, useUserSelector } from 'js/store/hooks';
import { useChangeLoginSessionLanguage } from 'server';
import { languageService } from 'js/services/language.service';
import { toggleIsChangeLangInProgress } from 'js/components/system/App/store/actions';
import { setLangAction } from 'js/hooks/user/store/actions';
import { storageService } from 'js/services/storage.service';

import * as constants from '../constants';

import { useSetLanguagePathname } from './useSetLanguagePathname';

export const useSetCurrentLanguage = () => {
    const changeLoginSessionLanguage = useChangeLoginSessionLanguage();
    const dispatch = useDispatch();
    const setLanguagePathname = useSetLanguagePathname();
    const { isLoggedIn, lang } = useUserSelector(pick(['lang', 'isLoggedIn']));
    const stayOnLangSwitch = useAppSelector(prop('stayOnLangSwitch'));

    return useCallback(async (langToSet: string): Promise<void> => {
        if (langToSet === lang) {
            return;
        }

        const languageCode = languageService.getTwoLetterLanguage(langToSet);

        if (isLoggedIn && languageCode) {
            try {
                await changeLoginSessionLanguage(languageCode);
            } catch (error) {
                log('error', error);
            }
        }

        dispatch(toggleIsChangeLangInProgress(true));
        setLanguagePathname(langToSet, stayOnLangSwitch);

        if (stayOnLangSwitch) {
            storageService.local.setItem(constants.USER_LANG_LOCAL_STORAGE_KEY, JSON.stringify(langToSet));

            dispatch(setLangAction(langToSet));
        }
    }, [lang, stayOnLangSwitch, isLoggedIn, setLanguagePathname, changeLoginSessionLanguage, dispatch]);
};
